import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { EmptyState, MessageXCircle, Pagination, SearchLg, Table, TextInput } from '@opendate-app/setlist';
import { toastError } from '../../../shared/toastHelper';

const axios = require('axios').default;
const qs = require('qs');

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const COLUMN_DEFS = [
  { data: 'created_at', searchable: true, orderable: true },
  { data: 'sent_to', searchable: false, orderable: false },
  { data: 'post', searchable: true, orderable: true },
  { data: 'calendar_events', searchable: false, orderable: false },
];

const buildParams = (page, sortDir, search) => ({
  draw: page,
  start: (page - 1) * PAGE_SIZE,
  length: PAGE_SIZE,
  search: { value: search, regex: false },
  columns: Object.fromEntries(COLUMN_DEFS.map((column, index) => [index, {
    data: column.data,
    name: '',
    searchable: column.searchable,
    orderable: column.orderable,
    search: { value: '', regex: false },
  }])),
  order: { 0: { column: 0, dir: sortDir } },
});

const formatDate = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const linkStyle = { color: 'var(--sl-text-brand)' };
const subtitleStyle = { fontWeight: 600, color: 'var(--sl-text-primary)' };

const COLUMNS = [
  { key: 'created_at', header: 'Date/Time', sortable: true, render: (row) => formatDate(row.created_at) },
  { key: 'sent_to', header: 'Sent To', render: (row) => Number(row.sent_to).toLocaleString('en-US') },
  {
    key: 'post',
    header: 'Message',
    primary: true,
    render: (row) => (
      <div>
        {row.subtitle ? <div style={subtitleStyle}>{row.subtitle}</div> : null}
        <div>{row.message}</div>
      </div>
    ),
  },
  {
    key: 'events',
    header: 'Events',
    render: (row) => (row.events || []).map((event, index) => (
      <React.Fragment key={event.id}>
        {index > 0 ? ', ' : null}
        {event.path ? <a href={event.path} style={linkStyle}>{event.title}</a> : event.title}
      </React.Fragment>
    )),
  },
];

const cardStyle = {
  marginTop: 24,
  background: 'var(--sl-bg-primary)',
  borderRadius: '12px',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  border: '1px solid var(--sl-border-secondary)',
};

const PushList = ({ dataSource, reloadKey, initialSearch, hasPushNotifications, canManage, onCreate, onTotalChange }) => {
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(1);
  const [sortDir, setSortDir] = useState('desc');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const latestRequest = useRef(0);

  useEffect(() => {
    if (searchInput === search) return undefined;
    const handle = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    axios.get(dataSource, {
      params: buildParams(page, sortDir, search),
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
    }).then(({ data }) => {
      if (requestId !== latestRequest.current) return;
      setRows(data.data || []);
      setTotal(data.recordsFiltered || 0);
      onTotalChange(data.recordsTotal || 0);
    }).catch(() => {
      if (requestId === latestRequest.current) toastError('Could not load push notifications, please try again');
    }).finally(() => {
      if (requestId === latestRequest.current) setLoading(false);
    });
  }, [page, sortDir, search, reloadKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstRun = !hasPushNotifications && reloadKey === 0 && !search;

  if (firstRun) {
    return (
      <div style={cardStyle}>
        <EmptyState
          icon={MessageXCircle}
          title="No Push Messages"
          description="Create your first push message to your venue app users."
          primaryAction={canManage ? { label: 'Create a Push Notification', onClick: onCreate } : undefined}
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden" style={cardStyle}>
      <div className="px-4 py-3 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 12 }}>
        <div style={{ width: 280 }}>
          <TextInput
            size="sm"
            leadingIcon={SearchLg}
            placeholder="Search"
            aria-label="Search push notifications"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <span className="figma-text sm">
          {total.toLocaleString('en-US')} {total === 1 ? 'push notification' : 'push notifications'}
        </span>
      </div>
      <div style={{ padding: '4px 8px' }}>
        <Table
          columns={COLUMNS}
          data={rows}
          getRowKey={(row) => row.id}
          loading={loading}
          loadingLabel="Loading push notifications"
          stackBelow={640}
          sort={{ key: 'created_at', direction: sortDir }}
          onSortChange={(_key, direction) => { setSortDir(direction); setPage(1); }}
          empty={<EmptyState size="sm" icon={SearchLg} title="No push notifications found" description="Try a different search." />}
        />
      </div>
      {totalPages > 1 && (
        <div className="px-4 py-3 border-top">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
};

PushList.propTypes = {
  dataSource: PropTypes.string.isRequired,
  reloadKey: PropTypes.number.isRequired,
  initialSearch: PropTypes.string,
  hasPushNotifications: PropTypes.bool.isRequired,
  canManage: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onTotalChange: PropTypes.func.isRequired,
};

PushList.defaultProps = { initialSearch: '', canManage: false };

export default PushList;
